import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  Database, Loader2, Search, AlertTriangle, Shield,
  RefreshCw, ChevronDown, ChevronRight, ExternalLink, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { runPIIScan, getLatestScan, getPIIDataMap, scanExternalDB, checkInternalCompliance } from '@/services/dpdpa';

const riskColor = (level) => {
  const map = { critical: 'bg-red-500/10 text-red-700 border-red-500/20', high: 'bg-orange-500/10 text-orange-700 border-orange-500/20', medium: 'bg-amber-500/10 text-amber-700 border-amber-500/20', low: 'bg-blue-500/10 text-blue-700 border-blue-500/20', none: 'bg-gray-500/10 text-gray-500 border-gray-500/20' };
  return map[level] || map.none;
};

const sensitivityIcon = (level) => {
  if (level === 'critical' || level === 'high') return <AlertTriangle className="w-3.5 h-3.5" />;
  return <Shield className="w-3.5 h-3.5" />;
};

export default function PIIScannerPage() {
  const [scan, setScan] = useState(null);
  const [dataMap, setDataMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showExternal, setShowExternal] = useState(false);
  const [extScanning, setExtScanning] = useState(false);
  const [extForm, setExtForm] = useState({ connection_string: '', db_name: '' });
  const [complianceResult, setComplianceResult] = useState(null);
  const [checking, setChecking] = useState(false);

  const fetchLatest = useCallback(async () => {
    try {
      setLoading(true);
      const [scanRes, mapRes] = await Promise.all([getLatestScan(), getPIIDataMap()]);
      setScan(scanRes);
      setDataMap(mapRes);
    } catch (err) {
      console.error('Failed to fetch scan data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  const handleScan = async () => {
    try {
      setScanning(true);
      toast.info('PII scan started...');
      const result = await runPIIScan();
      setScan(result);
      toast.success(`Scan complete: ${result.scan_summary?.total_pii_fields || 0} PII fields found`);
      fetchLatest();
    } catch (err) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleExternalScan = async () => {
    if (!extForm.connection_string || !extForm.db_name) {
      toast.error('Connection string and database name are required');
      return;
    }
    try {
      setExtScanning(true);
      toast.info('Scanning external database...');
      const result = await scanExternalDB(extForm);
      setScan(result);
      toast.success(`External scan complete: ${result.scan_summary?.total_pii_fields || 0} PII fields found`);
      setShowExternal(false);
      setExtForm({ connection_string: '', db_name: '' });
      fetchLatest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'External scan failed');
    } finally {
      setExtScanning(false);
    }
  };

  const toggleExpand = (collection) => {
    setExpanded(prev => ({ ...prev, [collection]: !prev[collection] }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin opacity-50" /></div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="pii-scanner-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">PII Data Scanner</h1>
          <p className="text-sm opacity-60">Scan databases for Personal Identifiable Information per DPDPA Section 2(t)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={async () => {
            try { setChecking(true); const r = await checkInternalCompliance(); setComplianceResult(r); toast.info(`Compliance: ${r.compliance_status.replace(/_/g, ' ')} — ${r.total_issues} issues`); } catch (err) { toast.error(err.response?.data?.detail || 'Check failed'); } finally { setChecking(false); }
          }} disabled={checking} data-testid="check-compliance-btn">
            {checking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />} Check Compliance
          </Button>
          <Button variant="outline" onClick={() => setShowExternal(true)} data-testid="scan-external-db-btn">
            <ExternalLink className="w-4 h-4 mr-2" /> External DB
          </Button>
          <Button onClick={handleScan} disabled={scanning} data-testid="run-pii-scan-btn">
            {scanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            {scanning ? 'Scanning...' : 'Run PII Scan'}
          </Button>
        </div>
      </div>

      {/* Summary */}
      {scan?.scan_summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="pii-scan-summary">
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{scan.scan_summary.total_collections}</p><p className="text-xs opacity-60">Collections</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold">{scan.scan_summary.total_documents}</p><p className="text-xs opacity-60">Documents</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-amber-500">{scan.scan_summary.total_pii_fields}</p><p className="text-xs opacity-60">PII Fields</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-orange-500">{scan.scan_summary.collections_with_pii}</p><p className="text-xs opacity-60">With PII</p></CardContent></Card>
          <Card><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-500">{scan.scan_summary.risk_collections}</p><p className="text-xs opacity-60">High Risk</p></CardContent></Card>
        </div>
      )}

      {/* Compliance Check Result */}
      {complianceResult && (
        <Card data-testid="compliance-result-card" className={`border-l-4 ${complianceResult.compliance_status === 'compliant' ? 'border-l-emerald-500' : complianceResult.compliance_status === 'non_compliant' ? 'border-l-red-500' : 'border-l-amber-500'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="w-4 h-4" /> DPDPA Non-Compliance Check
              <Badge className={complianceResult.compliance_status === 'compliant' ? 'bg-emerald-500/10 text-emerald-700' : complianceResult.compliance_status === 'non_compliant' ? 'bg-red-500/10 text-red-700' : 'bg-amber-500/10 text-amber-700'}>
                {complianceResult.compliance_status.replace(/_/g, ' ')}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 text-sm mb-3">
              <span>Total Issues: <strong>{complianceResult.total_issues}</strong></span>
              <span className="text-red-600">Critical: <strong>{complianceResult.critical_issues}</strong></span>
              <span className="text-orange-600">High: <strong>{complianceResult.high_issues}</strong></span>
              <span>Affected Collections: <strong>{complianceResult.collections_affected}</strong></span>
            </div>
            {complianceResult.issues?.slice(0, 5).map((item, idx) => (
              <div key={idx} className="border rounded p-2 mb-2 text-xs">
                <div className="font-medium mb-1">{item.collection} ({item.issue_count} issues)</div>
                {item.issues.slice(0, 3).map((iss, i) => (
                  <div key={i} className="flex items-start gap-2 ml-2 mb-1">
                    <Badge className={`text-xs ${iss.severity === 'critical' ? 'bg-red-500/10 text-red-700' : iss.severity === 'high' ? 'bg-orange-500/10 text-orange-700' : 'bg-amber-500/10 text-amber-700'}`}>{iss.severity}</Badge>
                    <span>{iss.description}</span>
                  </div>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Data Map Categories */}
      {dataMap?.categories && Object.keys(dataMap.categories).length > 0 && (
        <Card data-testid="pii-categories-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">PII Categories Found</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(dataMap.categories).map(([cat, info]) => (
                <Badge key={cat} variant="outline" className="capitalize text-sm py-1 px-3">
                  {cat.replace(/_/g, ' ')} <span className="ml-1 opacity-60">({info.count})</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Collection Results */}
      {scan?.collections && (
        <div className="space-y-2" data-testid="pii-collection-results">
          {scan.collections.map((coll) => (
            <Card key={coll.collection} className={coll.pii_field_count > 0 ? 'border-l-4 ' + (coll.risk_level === 'critical' ? 'border-l-red-500' : coll.risk_level === 'high' ? 'border-l-orange-500' : 'border-l-amber-500') : ''}>
              <CardContent className="p-4">
                <button className="w-full flex items-center justify-between" onClick={() => toggleExpand(coll.collection)}>
                  <div className="flex items-center gap-3">
                    <Database className="w-4 h-4 opacity-50" />
                    <span className="font-medium">{coll.collection}</span>
                    <Badge className={riskColor(coll.risk_level)}>{coll.risk_level}</Badge>
                    <span className="text-xs opacity-50">{coll.doc_count} docs, {coll.total_fields || 0} fields</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {coll.pii_field_count > 0 && (
                      <Badge variant="destructive" className="text-xs">{coll.pii_field_count} PII</Badge>
                    )}
                    {expanded[coll.collection] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </div>
                </button>
                {expanded[coll.collection] && coll.pii_fields && coll.pii_fields.length > 0 && (
                  <div className="mt-3 border-t pt-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs opacity-60">
                          <th className="pb-2">Field</th>
                          <th className="pb-2">PII Type</th>
                          <th className="pb-2">Category</th>
                          <th className="pb-2">Sensitivity</th>
                          <th className="pb-2">Detected By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coll.pii_fields.map((pii, idx) => (
                          <tr key={`${pii.field}-${idx}`} className="border-t">
                            <td className="py-2 font-mono text-xs">{pii.field}</td>
                            <td className="py-2">{pii.label}</td>
                            <td className="py-2 capitalize text-xs">{pii.category?.replace(/_/g, ' ')}</td>
                            <td className="py-2">
                              <Badge className={`text-xs ${riskColor(pii.sensitivity)}`}>
                                {sensitivityIcon(pii.sensitivity)} <span className="ml-1">{pii.sensitivity}</span>
                              </Badge>
                            </td>
                            <td className="py-2 text-xs opacity-60">{pii.detected_by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!scan?.collections && (
        <Card><CardContent className="p-8 text-center opacity-50"><Database className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No scan results. Click "Run PII Scan" to analyze your database.</p></CardContent></Card>
      )}

      {/* External DB Dialog */}
      <Dialog open={showExternal} onOpenChange={setShowExternal}>
        <DialogContent data-testid="external-db-dialog">
          <DialogHeader><DialogTitle>Scan External MongoDB Database</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>MongoDB Connection String</Label>
              <Input value={extForm.connection_string} onChange={e => setExtForm(prev => ({ ...prev, connection_string: e.target.value }))} placeholder="mongodb://user:pass@host:27017" type="password" data-testid="ext-db-connection-input" />
              <p className="text-xs opacity-50 mt-1">Connection will timeout after 10 seconds if unreachable</p>
            </div>
            <div>
              <Label>Database Name</Label>
              <Input value={extForm.db_name} onChange={e => setExtForm(prev => ({ ...prev, db_name: e.target.value }))} placeholder="e.g. production_db" data-testid="ext-db-name-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExternal(false)}>Cancel</Button>
            <Button onClick={handleExternalScan} disabled={extScanning} data-testid="run-external-scan-btn">
              {extScanning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              Scan Database
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
